package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.auditlog.AuditLogEntryDto

data class LogSummary(val strong: String, val plain: String)

data class LogDetailRow(val key: String, val value: String, val mono: Boolean = true)

object AuditLogView {
    const val SYSTEM_ACTOR = "System"
    const val SEARCH_PLACEHOLDER = "Search actor, action, target…"
    const val CLEAR_FILTERS = "Clear filters"
    const val LOAD_MORE = "Load more"
    const val LOADING_MORE = "Loading..."
    const val RESOURCE = "the audit log"

    val filterHelp: String =
        "Search narrows the loaded rows only. ${AuditExportCopy.HINT}"

    fun isServerFiltered(filter: AuditFilter): Boolean =
        listOf(filter.actionPrefix, filter.actorUserId, filter.since, filter.until).any { !it.isNullOrBlank() }

    fun isFiltered(filter: AuditFilter, query: String): Boolean = query.isNotBlank() || isServerFiltered(filter)

    fun visibleEntries(entries: List<AuditLogEntryDto>, query: String): List<AuditLogEntryDto> =
        if (query.isBlank()) entries else entries.filter { matches(it, query) }

    fun summary(shown: Int, hasMore: Boolean, filter: AuditFilter, query: String): LogSummary {
        val searching = query.isNotBlank()
        val atLeast = hasMore && !searching
        val single = shown == 1 && !atLeast
        val strong = when (shown) {
            0 -> "No events"
            else -> "$shown${if (atLeast) "+" else ""} ${if (single) "event" else "events"}"
        }
        val verb = if (single) "matches" else "match"
        val plain = when {
            searching && hasMore -> " $verb your search in the loaded rows."
            searching -> " $verb your search."
            isServerFiltered(filter) -> " $verb these filters."
            shown == 0 -> " yet."
            else -> " in the audit log."
        }
        return LogSummary(strong, plain)
    }

    fun emptyText(filter: AuditFilter, query: String, hasMore: Boolean): String = when {
        query.isNotBlank() && hasMore -> "No loaded events match your search. Load more to look further back."
        query.isNotBlank() -> "No events match your search."
        isServerFiltered(filter) -> "No events match these filters."
        else -> "No events yet. Invites, roster changes and standup edits will appear here."
    }

    fun actorLabel(entry: AuditLogEntryDto): String = entry.actorDisplayName ?: SYSTEM_ACTOR

    fun targetLabel(entry: AuditLogEntryDto): String? = when {
        entry.targetType.isNullOrBlank() -> null
        entry.targetId.isNullOrBlank() -> entry.targetType
        else -> "${entry.targetType} · ${entry.targetId}"
    }

    fun detailRows(entry: AuditLogEntryDto): List<LogDetailRow> = buildList {
        add(LogDetailRow("id", entry.id.toString()))
        add(LogDetailRow("time", entry.createdAt.toString()))
        entry.summary?.let { add(LogDetailRow("summary", it, mono = false)) }
        entry.ipAddress?.let { add(LogDetailRow("ip", it)) }
        entry.actorUserId?.let { add(LogDetailRow("actorUserId", it)) }
    }

    private fun matches(entry: AuditLogEntryDto, query: String): Boolean =
        listOfNotNull(entry.actorDisplayName, entry.action, entry.targetType, entry.targetId, entry.summary)
            .any { it.contains(query, ignoreCase = true) }
}
